import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate, faEnvelope, faPlus } from "@fortawesome/free-solid-svg-icons";
import config from "./config";
import { usePersonalServices } from "./personalServices";
import { APPEARANCE_CHANGE_EVENT, getAppearancePreference, saveAppearancePreference } from "./plansAppearance";
import { meetingApi, meetingOperationId } from "./meetingApi";
import MeetingServiceStatus from "./MeetingServiceStatus";
import MeetingAnalysis from "./MeetingAnalysis";
import RemoteAnalysisButton from "./RemoteAnalysisButton";
import MemberSignalMetrics from "./MemberSignalMetrics";
import SignalScoreGauge from "./SignalScoreGauge";
import MeetingSelector from "./MeetingSelector";
import MeetingServiceSelector from "./MeetingServiceSelector";
import useSharedAnalyses from "./useSharedAnalyses";
import "./Services.css";
import "./TeamManagement.css";
import "./Meetings.css";

function NewMeeting({ services, team, onCreated, onCancel }) {
  const [name, setName] = useState(() => {
    const fullName = [localStorage.getItem("userFirstName"), localStorage.getItem("userLastName")].filter(Boolean).map(value => value.trim()).filter(Boolean).join(" ");
    return fullName ? `${fullName}'s meeting`.slice(0, 120) : "My meeting";
  });
  const [serviceId, setServiceId] = useState("");
  const [email, setEmail] = useState("");
  const [invitees, setInvitees] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const creation = useRef(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  function addInvitee(address) {
    const normalized = address.trim().toLowerCase();
    if (invitees.some(person => person.email === normalized)) { setError("This participant is already on the invitation list."); return; }
    if (invitees.length >= 99) { setError("A meeting can have up to 100 participants, including you. You can invite 99 others."); return; }
    setInvitees(current => [...current, { email: normalized, operationId: meetingOperationId() }]);
    setEmail(""); setError("");
  }
  async function start(event) {
    event.preventDefault();
    if (inFlight.current || !serviceId) return;
    inFlight.current = true; setBusy(true); setError("");
    // Keep the exact creation request on an uncertain response so retry cannot create a second meeting.
    creation.current ||= { meetingId: meetingOperationId(), name: name.trim(), serviceId: Number(serviceId) };
    try {
      const result = await meetingApi("", "POST", creation.current);
      const remaining = [];
      for (const person of invitees) {
        if (result.participants?.some(participant => String(participant.userId) === String(result.meeting.creatorUserId) && participant.status === "Accepted" && participant.email?.trim().toLowerCase() === person.email)) continue;
        try { await meetingApi(`/${result.meeting.meetingId}/invitations`, "POST", person); }
        catch { remaining.push(person); }
      }
      if (mounted.current) onCreated(result.meeting.meetingId, remaining);
    } catch (reason) {
      const rejected = [400, 401, 403, 422, 429].includes(reason.status);
      if (rejected) creation.current = null;
      if (mounted.current) setError(rejected ? reason.message : `${reason.message} Retry Start meeting analysis to confirm this same meeting.`);
    } finally {
      inFlight.current = false; if (mounted.current) setBusy(false);
    }
  }
  const locked = busy || Boolean(creation.current);
  function cancel() {
    if (locked) return;
    if (onCancel) { onCancel(); return; }
    setServiceId(""); setInvitees([]); setEmail(""); setError("");
  }
  const available = (team || []).filter(member => member.UserEmail && String(member.UserId) !== localStorage.getItem("userID") && !invitees.some(person => person.email === member.UserEmail.trim().toLowerCase()));
  return <section className="service-monitor-panel meeting-editor" aria-label="New meeting">
    <form id="new-meeting" className="meeting-section" onSubmit={start}>
      <h2>Step 1: Create a meeting to analyze</h2>
      <div className="meeting-fields">
        <label>Meeting name<input required maxLength={120} value={name} disabled={locked} onChange={event => setName(event.target.value)} /></label>
        <MeetingServiceSelector services={services} value={serviceId} disabled={locked} onChange={setServiceId} />
      </div>
    </form>
    <section className="meeting-section" aria-labelledby="new-meeting-invite-heading">
      <h3 id="new-meeting-invite-heading">Step 2: Invite participants</h3><p>You will be included automatically. Add other participants now. Invitations will be sent after you start the meeting.</p>
      <form className="meeting-invite-form" onSubmit={event => { event.preventDefault(); addInvitee(email); }}>
        <label>Participant email<input type="email" required maxLength={254} value={email} disabled={locked} onChange={event => setEmail(event.target.value)} autoComplete="off" /></label>
        <button className="meeting-add-invite" type="submit" disabled={locked || !email.trim()}><FontAwesomeIcon icon={faPlus} aria-hidden="true" />Add to invite list</button>
      </form>
      {available.length > 0 && <label className="meeting-team-picker">Invite from your team<select value="" disabled={locked} onChange={event => { if (event.target.value) addInvitee(event.target.value); }}>
        <option value="">Choose a team member</option>
        {available.map(member => <option key={member.UserId} value={member.UserEmail}>{`${member.UserFirstName || ""} ${member.UserLastName || ""}`.trim() || member.UserEmail}</option>)}
      </select></label>}
      <ul className="meeting-team">{invitees.map(person => <li key={person.operationId}><span>{person.email}</span>
        <button type="button" disabled={locked} aria-label={`Remove ${person.email}`} onClick={() => setInvitees(current => current.filter(value => value.operationId !== person.operationId))}>Remove</button>
      </li>)}</ul>
    </section>
    <section className="meeting-section" aria-labelledby="new-meeting-start-heading">
      <h3 id="new-meeting-start-heading">Step 3: Start meeting analysis</h3>
      {error && <p role="alert">{error}</p>}
      {email.trim() && <p>Add the entered email to the invitation list or clear it before starting.</p>}
      <div className="meeting-start-actions">
        <button className="meeting-start-button" type="submit" form="new-meeting" disabled={busy || !name.trim() || !serviceId || Boolean(email.trim())}>{busy ? "Starting..." : "Start meeting analysis"}</button>
        {(!serviceId || invitees.length === 0) && <div className="meeting-required" role="status">
          <p><strong>Required</strong></p>
          <ul>
            {!serviceId && <li>Select a meeting service</li>}
            {invitees.length === 0 && <li>Invite at least one participant</li>}
          </ul>
        </div>}
      </div>
    </section>
    <footer className="meeting-section meeting-create-footer">
      <button type="button" disabled={locked} onClick={cancel}>Cancel</button>
    </footer>
  </section>;
}

function MeetingEditor({ id, services, team, onDeleted, unsentInvites, onInvitesChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(Boolean(id)); const [busy, setBusy] = useState(false); const [error, setError] = useState("");
  const [email, setEmail] = useState(""); const [notice, setNotice] = useState(""); const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [requests, setRequests] = useState({}); const operation = useRef(null); const inFlight = useRef(false);
  const sharedAnalyses = useSharedAnalyses({ apiBaseUrl: config.API_URL, token: localStorage.getItem("token") || "",
    enabled: Boolean(detail && id), webMeetingId: id });
  const mounted = useRef(true);
  const backgroundRefresh = useRef(null);
  const [telemetryError, setTelemetryError] = useState("");
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const apply = useCallback(value => {
    setDetail(value);
    setRequests(Object.fromEntries((value.requests || []).map(r => [r.targetUserId, r])));
  }, []);
  useEffect(() => {
    if (!id) return;
    const controller = new AbortController();
    meetingApi(`/${id}`, "GET", undefined, controller.signal).then(value => { if (!controller.signal.aborted) apply(value); })
      .catch(reason => { if (!controller.signal.aborted) setError(reason.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [id, apply]);
  useEffect(() => {
    async function poll() {
      if (document.hidden || inFlight.current || backgroundRefresh.current) return;
      const controller = new AbortController();
      backgroundRefresh.current = controller;
      try {
        const value = await meetingApi(`/${id}`, "GET", undefined, controller.signal);
        if (!controller.signal.aborted) {
          // Keep active analysis tracking independent of status polling.
          setDetail(current => current ? { ...current, meeting: value.meeting, canManage: value.canManage, participants: value.participants } : value);
          setTelemetryError("");
        }
      } catch (reason) {
        if (!controller.signal.aborted) {
          if ([401, 403, 404].includes(reason.status)) { setDetail(null); setError(reason.message); }
          else {
            setDetail(current => current && { ...current, participants: current.participants.map(person => ({ ...person, signal: null })) });
            setTelemetryError("Participant status could not be refreshed. Retrying automatically.");
          }
        }
      } finally { if (backgroundRefresh.current === controller) backgroundRefresh.current = null; }
    }
    const timer = setInterval(poll, 30000);
    document.addEventListener("visibilitychange", poll);
    return () => {
      clearInterval(timer); document.removeEventListener("visibilitychange", poll);
      backgroundRefresh.current?.abort(); backgroundRefresh.current = null;
    };
  }, [id]);
  async function mutate(action) {
    if (inFlight.current) return;
    backgroundRefresh.current?.abort(); backgroundRefresh.current = null;
    inFlight.current = true; setBusy(true); setError(""); setNotice("");
    try { await action(); }
    catch (reason) { if (mounted.current) setError(reason.name === "AbortError" ? "The response could not be confirmed. Retry the same action." : reason.message); }
    finally { inFlight.current = false; if (mounted.current) setBusy(false); }
  }
  async function refresh() { const value = await meetingApi(`/${id}`); if (mounted.current) { apply(value); setTelemetryError(""); } }
  const participants = detail?.participants || []; const canManage = detail?.canManage;
  const canAnalyze = detail?.canAnalyze ?? canManage;
  const available = (team || []).filter(member => member.UserEmail && !participants.some(person =>
    String(person.userId) === String(member.UserId) || person.email?.trim().toLowerCase() === member.UserEmail.trim().toLowerCase()));
  const serviceId = detail?.meeting.serviceId;
  function retainedOperation(kind, value) {
    if (!operation.current || operation.current.kind !== kind || operation.current.value !== value)
      operation.current = { kind, value, id: meetingOperationId() };
    return operation.current.id;
  }
  if (loading) return <section className="service-monitor-panel meeting-section" role="status">Loading meeting...</section>;
  if (id && !detail) return <section className="service-monitor-panel meeting-section"><p role="alert">{error || "Meeting unavailable."}</p></section>;
  const participantsHeading = <div><h2>Participants</h2><p>{participants.length} {participants.length === 1 ? "participant" : "participants"}</p></div>;
  return <section className="service-monitor-panel meeting-editor" aria-label="Meeting details">
    <header className="service-monitor-toolbar meeting-instance-header"><div><h2>{detail.meeting.name}</h2>
      {error && <p role="alert">{error}</p>}{telemetryError && <p role="alert">{telemetryError}</p>}{notice && <p role="status">{notice}</p>}
    </div></header>
      <MeetingServiceStatus serviceId={serviceId} service={services.find(service => String(service.ServiceId) === String(serviceId))} />
      <section className="meeting-participants" aria-label="Meeting participants">
        {canAnalyze ? <MeetingAnalysis meetingId={id} participants={participants} disabled={busy}
          onAccepted={results => setRequests(current => ({ ...current, ...Object.fromEntries(results.filter(r => r.request).map(r => [r.targetUserId, r.request])) }))}
          renderInline={({ action, panel }) => <><div className="service-monitor-toolbar">{participantsHeading}{action}</div>{panel}</>}
        /> : <div className="service-monitor-toolbar">{participantsHeading}</div>}
        {!participants.length && <p className="meeting-empty">No participants yet. Add team members or invite someone below.</p>}
        <ul className="service-monitor-list team-member-list" aria-label="Meeting participant list">{participants.map(person => {
          const accepted = person.status === "Accepted";
          const emailOnly = !accepted && (!person.name || person.name.toLowerCase() === person.email?.toLowerCase());
          const emailCharacters = Array.from(person.email || person.name || "");
          const emailPreview = emailCharacters.length > 25 ? `${emailCharacters.slice(0, 25).join("")}…` : emailCharacters.join("");
          const expired = !accepted && person.expiresAt && new Date(person.expiresAt) <= new Date();
          // Missing field supports older backends; explicit null must not bypass meeting access checks.
          const signal = !accepted ? undefined : person.signal === undefined
            ? team?.find(member => String(member.UserId) === String(person.userId))
            : person.signal && {
              SignalScore: person.signal.signalScore, SignalWifiStatus: person.signal.signalWifiStatus,
              SignalBandwidthStatus: person.signal.signalBandwidthStatus, SignalSystemStatus: person.signal.signalSystemStatus,
              SignalOverallStatus: person.signal.signalOverallStatus
            };
          const renderRow = ({ action, panel } = {}) => <li key={person.participantId} className="service-monitor-row team-member-row meeting-participant-row">
            <div className="team-member-row-main">
              <div className="service-monitor-identity"><span className="team-member-avatar" aria-hidden="true">{person.name.split(/\s+/).slice(0, 2).map(part => part[0]).join("").toUpperCase()}</span>
                <div className="meeting-participant-identity"><h3 className={emailOnly ? "meeting-invite-email" : undefined} title={emailOnly ? person.email || person.name : undefined} aria-label={emailOnly ? person.email || person.name : undefined}>{emailOnly ? emailPreview : person.name}</h3>{!emailOnly && person.email && person.email !== person.name && <p className="team-member-email" title={person.email}>{person.email}</p>}
                  <span className={`service-monitor-status service-monitor-status--${accepted ? "healthy" : expired ? "unknown" : "incident"}`}><span aria-hidden="true" />{accepted ? "Accepted" : expired ? "Invitation expired" : "Invitation pending"}</span>
                  {!accepted && <p className="meeting-delivery-status">{person.emailState === "Accepted" ? "Invitation sent" : person.emailState === "Sending" ? "Email delivery not yet confirmed" : "Email delivery could not be confirmed"}</p>}
                </div>
              </div>
              <MemberSignalMetrics signal={signal} name={person.name} renderScore={score => <SignalScoreGauge score={score} />} />
              {(canManage || canAnalyze) && <div className="team-row-actions" role="group" aria-label={`Actions for ${person.name}`}>
                {action}
                {canManage && !accepted && <button className="meeting-resend-invitation" type="button" disabled={busy} onClick={() => mutate(async () => {
              const operationId = retainedOperation("resend", person.participantId);
              const value = await meetingApi(`/${id}/invitations/${person.participantId}/resend`, "POST", { operationId });
              if (mounted.current) { apply(value); operation.current = null; }
            })}><FontAwesomeIcon icon={faEnvelope} aria-hidden="true" />Resend invite</button>}
              {canManage && <button type="button" disabled={busy} aria-label={`Remove ${person.name}`} onClick={() => mutate(async () => { await meetingApi(`/${id}/participants/${person.participantId}`, "DELETE"); await refresh(); })}>Remove</button>}
              </div>}
            </div>
            {panel}
            {!canManage && String(person.userId) === localStorage.getItem("userID") && <p className="meeting-participant-note">Keep SignalTuner open on your device to receive analysis requests from meeting participants.</p>}
          </li>;
          return accepted && canAnalyze ? <RemoteAnalysisButton key={person.participantId} apiBaseUrl={config.API_URL} token={localStorage.getItem("token") || ""}
            targetUserId={person.userId} targetName={person.name} webMeetingId={id} backgroundRequest={sharedAnalyses[person.userId] || requests[person.userId]}
            renderInline={renderRow} /> : renderRow();
        })}</ul>
        {canManage && unsentInvites.length > 0 && <div className="meeting-invitation-retries">
          <p role="alert">The meeting was created, but some invitations could not be confirmed. Retry to check or send them without duplicating confirmed invitations.</p>
          <ul className="meeting-team">{unsentInvites.map(person => <li key={person.operationId}><span>{person.email} — Invitation not confirmed</span>
            <button type="button" disabled={busy} aria-label={`Retry invitation to ${person.email}`} onClick={() => mutate(async () => {
              const value = await meetingApi(`/${id}/invitations`, "POST", { email: person.email, operationId: person.operationId });
              if (mounted.current) { apply(value); onInvitesChanged(unsentInvites.filter(item => item.operationId !== person.operationId)); }
            })}>Retry invitation</button>
          </li>)}</ul>
        </div>}
      </section>
      {canManage && <details className="meeting-invites">
        <summary>Invite participants</summary>
        <div className="meeting-section">
        <form className="meeting-invite-form" onSubmit={event => { event.preventDefault(); mutate(async () => {
          const operationId = retainedOperation("invite", email.trim().toLowerCase());
          const value = await meetingApi(`/${id}/invitations`, "POST", { email, operationId });
          if (mounted.current) { apply(value); setEmail(""); operation.current = null; setNotice("Invitation created. Check its delivery status in the participant list."); }
        }); }}><label>Participant email<input type="email" required maxLength={254} value={email} disabled={busy} onChange={event => setEmail(event.target.value)} autoComplete="off" /></label>
          <button type="submit" disabled={busy || !email.trim()}>Send invitation</button>
        <label className="meeting-team-picker">Team members to invite<select disabled={busy || !available.length}
          value={available.some(member => member.UserEmail.trim().toLowerCase() === email.trim().toLowerCase()) ? email.trim().toLowerCase() : ""}
          onChange={event => setEmail(event.target.value)}>
          <option value="">{available.length ? "Choose a team member" : "No team members available to invite"}</option>
          {available.map(member => <option key={member.UserId} value={member.UserEmail.trim().toLowerCase()}>{`${member.UserFirstName || ""} ${member.UserLastName || ""}`.trim() || member.UserEmail}</option>)}
        </select></label></form>
        <p className="meeting-note">Choose a team member to fill in their email, then send the invitation. Participants must accept their invitation before analysis.</p>
        <p className="meeting-note">Invitations expire after seven days. Resending invalidates the previous link and may send another email if earlier delivery was uncertain.</p>
        </div>
      </details>}
      <section className="meeting-section meeting-footer" aria-label="Meeting actions"><div className="meeting-footer-actions">
        <button className="meeting-refresh" type="button" disabled={busy} onClick={() => mutate(refresh)}><FontAwesomeIcon icon={faArrowsRotate} aria-hidden="true" />Refresh meeting</button>
        {canManage && <button className="meeting-delete-button" type="button" disabled={busy} onClick={() => setDeleteConfirm(value => !value)}>Delete meeting</button>}
        </div>
        {canManage && deleteConfirm && <div className="meeting-delete-confirmation"><p>Delete this meeting and its invitations? Active analyses will be cancelled and reserved credits returned. Completed reports and charges remain.</p>
          <button type="button" disabled={busy} onClick={() => mutate(async () => { await meetingApi(`/${id}`, "DELETE"); if (mounted.current) onDeleted(); })}>Confirm delete meeting</button>
          <button type="button" disabled={busy} onClick={() => setDeleteConfirm(false)}>Keep meeting</button></div>}
      </section>
  </section>;
}

export default function Meetings({ team }) {
  const currentUserId = localStorage.getItem("userID");
  const otherTeamMembers = (team || []).filter(member => String(member.UserId) !== currentUserId);
  const { services, error: servicesError } = usePersonalServices(); const [appearance, setAppearance] = useState(getAppearancePreference);
  const [meetings, setMeetings] = useState([]); const [error, setError] = useState(""); const [loading, setLoading] = useState(true);
  const [search, setSearch] = useSearchParams();
  const [unsentInvites, setUnsentInvites] = useState({});
  const selected = search.get("meeting") || (search.get("new") === "1" ? "" : meetings[0]?.meetingId || "");
  const lastOpenMeeting = useRef({ userId: currentUserId, meetingId: "" });
  useEffect(() => {
    if (lastOpenMeeting.current.userId !== currentUserId) lastOpenMeeting.current = { userId: currentUserId, meetingId: "" };
    if (selected) lastOpenMeeting.current.meetingId = selected;
  }, [selected, currentUserId]);
  function cancelNewMeeting() {
    const previous = lastOpenMeeting.current.userId === currentUserId ? lastOpenMeeting.current.meetingId : "";
    const target = meetings.find(meeting => meeting.meetingId === previous) || meetings[0];
    if (target) setSearch({ meeting: target.meetingId });
  }
  const refresh = useCallback(async signal => {
    try { const result = await meetingApi("", "GET", undefined, signal); if (!signal?.aborted) { setMeetings(result.meetings); setError(""); } }
    catch (reason) { if (!signal?.aborted) setError(reason.message); }
    finally { if (!signal?.aborted) setLoading(false); }
  }, []);
  useEffect(() => { const controller = new AbortController(); refresh(controller.signal); return () => controller.abort(); }, [refresh]);
  useEffect(() => { const update = () => setAppearance(getAppearancePreference()); window.addEventListener(APPEARANCE_CHANGE_EVENT, update); window.addEventListener("storage", update);
    return () => { window.removeEventListener(APPEARANCE_CHANGE_EVENT, update); window.removeEventListener("storage", update); }; }, []);
  return <main className={`service-monitor-page service-monitor-page--${appearance} meetings-page`}><div className="service-monitor-inner">
    {(meetings.length > 0 || selected) && <header className="service-monitor-header"><div><p className="service-monitor-eyebrow">MEETINGS</p><h1>A clearer view of your meeting.</h1><p className="service-monitor-intro">Check your service, bring people together, and analyze everyone's connection. Your meetings stay here for next time.</p></div>
      <div className="service-monitor-theme" role="group" aria-label="Website appearance">{["light", "dark"].map(theme => <button key={theme} type="button" aria-pressed={appearance === theme} onClick={() => saveAppearancePreference(theme)}>{theme === "light" ? "Light" : "Dark"}</button>)}</div>
    </header>}
    {(error || servicesError) && <p role="alert">{error || servicesError} <button type="button" onClick={() => refresh()}>Retry meetings</button></p>}
    {loading ? <p role="status">Loading meetings...</p> : <div className={`meetings-layout${selected ? "" : " meetings-layout--new"}`}>
      <div className="meeting-content">{selected ? <MeetingEditor key={selected} id={selected} services={services} team={otherTeamMembers}
        unsentInvites={unsentInvites[selected] || []} onInvitesChanged={value => setUnsentInvites(current => ({ ...current, [selected]: value }))}
        onDeleted={async () => { await refresh(); setSearch({}); }} />
      : <NewMeeting services={services} team={otherTeamMembers} onCancel={meetings.length ? cancelNewMeeting : undefined} onCreated={(id, remaining) => {
        setUnsentInvites(current => ({ ...current, [id]: remaining })); setSearch({ meeting: id }); refresh();
      }} />}</div>
      {(meetings.length > 0 || selected) && <div className="meeting-navigation">
        {meetings.length > 0 && (meetings.length > 1 || !selected) && <MeetingSelector meetings={meetings} selected={selected} onChange={meeting => setSearch({ meeting })} />}
        {selected && <button className="meeting-another" type="button" onClick={() => setSearch({ new: "1" })}><FontAwesomeIcon icon={faPlus} aria-hidden="true" />New meeting</button>}
      </div>}
    </div>}
  </div></main>;
}
